import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Upload, AlertCircle, FileText } from "lucide-react";
import { parseZipFile } from "./utils/parse-zip";
import { PdfComparisonResults } from "./components/pdf-comparison-results.component";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { isDeveloper } from "@/lib/utils/is-app-user";

interface ParsedPdf {
	orgId: string;
	pdfType: string;
	blob: Blob;
	filename: string;
}

export const PdfDiffPage: React.FC = () => {
	const [zip1Pdfs, setZip1Pdfs] = useState<ParsedPdf[]>([]);
	const [zip2Pdfs, setZip2Pdfs] = useState<ParsedPdf[]>([]);
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Localhost check
	if (!isDeveloper(import.meta.env)) {
		return (
			<div className="p-8">
				<div className="bg-red-100 border-2 border-red-600 rounded-lg p-6">
					<div className="flex items-center gap-3">
						<AlertCircle className="w-6 h-6 text-red-600" />
						<div>
							<h2 className="text-lg font-semibold text-red-950">
								Nur auf localhost verfügbar
							</h2>
							<p className="text-sm text-red-900 mt-1">
								Diese Seite ist nur in der lokalen Entwicklungsumgebung zugänglich.
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const handleFileUpload = async (file: File, zipNumber: 1 | 2) => {
		try {
			setIsProcessing(true);
			setError(null);

			const pdfs = await parseZipFile(file);

			if (zipNumber === 1) {
				setZip1Pdfs(pdfs);
			} else {
				setZip2Pdfs(pdfs);
			}
		} catch (err) {
			setError(`Fehler beim Verarbeiten von ZIP ${zipNumber}: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
		} finally {
			setIsProcessing(false);
		}
	};

	const handleClear = () => {
		setZip1Pdfs([]);
		setZip2Pdfs([]);
		setError(null);
	};

	return (
		<div className="p-8 max-w-7xl mx-auto">
				<HeaderPortal>	
				<PageTitle
					title={[{ name: "Support", link: "/support" }, { name: "PDF Vergleich" }]}
					icon={<FileText width="16px" height="16px" color="rgb(55, 65, 81)" />}
				/>
			</HeaderPortal>
			<div className="mb-8">
				<h1 className="text-2xl font-bold text-gray-900 mb-2">
					PDF Vergleich
				</h1>
				<p className="text-gray-600">
					Laden Sie zwei PDF-ZIP-Dateien hoch, um sie pixelgenau zu vergleichen.
				</p>
			</div>

			{error && (
				<div className="bg-red-100 border-2 border-red-600 rounded-lg p-4 mb-6">
					<div className="flex items-center gap-2">
						<AlertCircle className="w-5 h-5 text-red-600" />
						<p className="text-sm text-red-900">{error}</p>
					</div>
				</div>
			)}

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
				<FileUploadArea
					label="ZIP 1 (Referenz)"
					onFileSelect={(file) => handleFileUpload(file, 1)}
					pdfCount={zip1Pdfs.length}
					isProcessing={isProcessing}
				/>
				<FileUploadArea
					label="ZIP 2 (Vergleich)"
					onFileSelect={(file) => handleFileUpload(file, 2)}
					pdfCount={zip2Pdfs.length}
					isProcessing={isProcessing}
				/>
			</div>

			{(zip1Pdfs.length > 0 || zip2Pdfs.length > 0) && (
				<div className="mb-6">
					<Button
						onClick={handleClear}
						variant="outline"
						size="sm"
					>
						Zurücksetzen
					</Button>
				</div>
			)}

			{zip1Pdfs.length > 0 && zip2Pdfs.length > 0 && (
				<PdfComparisonResults
					zip1Pdfs={zip1Pdfs as any}
					zip2Pdfs={zip2Pdfs as any}
				/>
			)}
		</div>
	);
};

interface FileUploadAreaProps {
	label: string;
	onFileSelect: (file: File) => void;
	pdfCount: number;
	isProcessing: boolean;
}

const FileUploadArea: React.FC<FileUploadAreaProps> = ({
	label,
	onFileSelect,
	pdfCount,
	isProcessing,
}) => {
	const { isDragging, dragHandlers } = useFileUpload({
		onFileUpload: (files) => {
			if (files.length > 0 && files[0].name.toLowerCase().endsWith('.zip')) {
				onFileSelect(files[0]);
			}
		},
	});

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			onFileSelect(file);
		}
	};

	return (
		<div
			className={`bg-white border-2 border-dashed rounded-lg p-8 transition-all ${
				isDragging
					? 'border-blue-500 bg-blue-50 scale-105'
					: 'border-gray-300 hover:border-blue-400'
			} ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
			{...dragHandlers}
		>
			<div className="flex flex-col items-center justify-center gap-4">
				<Upload className={`w-12 h-12 transition-colors ${
					isDragging ? 'text-blue-500' : 'text-gray-400'
				}`} />
				<div className="text-center">
					<h3 className="font-semibold text-gray-900 mb-1">{label}</h3>
					{pdfCount > 0 ? (
						<p className="text-sm text-green-600">
							{pdfCount} PDF(s) geladen
						</p>
					) : (
						<p className="text-sm text-gray-600">
							{isDragging ? 'Datei hier ablegen...' : 'ZIP-Datei hochladen oder hierher ziehen'}
						</p>
					)}
				</div>
				<label>
					<input
						type="file"
						accept=".zip"
						onChange={handleChange}
						disabled={isProcessing}
						className="hidden"
						aria-label={`Upload ${label}`}
						data-testid={`upload-${label.toLowerCase().replace(/\s+/g, '-')}`}
					/>
					<Button
						type="button"
						variant="outline"
						size="sm"
						disabled={isProcessing}
						onClick={(e) => {
							e.preventDefault();
							(e.currentTarget.previousElementSibling as HTMLInputElement)?.click();
						}}
					>
						Datei auswählen
					</Button>
				</label>
			</div>
		</div>
	);
};
